import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { FileUpload } from 'primereact/fileupload';
import axios from 'axios';
import { url, api } from '../../../../env';

export default function UserPaymentUpload({ step, financialDetails, onUpdate, toast }) {
    const [amount, setAmount] = useState(0);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [reasonConfig, setReasonConfig] = useState({ visible: false, text: '' });
    const fileUploadRef = useRef(null);
    const totalDownPayment = Number(financialDetails?.groupConceptsEnganche?.totalWitIva) || 0;
    const roundedDownPayment = Math.round(totalDownPayment * 100) / 100;
    const totalPaid = step?.total_paid || 0;
    let remaining = Math.max(0, roundedDownPayment - totalPaid);
    remaining = Math.round(remaining * 100) / 100;

    const hasPending = step?.payments?.some(p => p.status === 'pending');
    const lastPayment = step?.payments?.[0]; // Newest first

    // Simplified validation: just disable button if invalid
    const isAmountInvalid = amount > remaining;

    if (totalDownPayment <= 0 && !step) {
        // Fallback or loading state might be needed, but essentially this is an error in data setup
        return <div className="p-3 bg-red-100 text-red-700 border-round">Error: No se encontró el monto del enganche. Por favor contacte a soporte.</div>;
    }

    const onSelectFile = (e) => {
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
        const validFiles = e.files.filter(file => {
            if (!allowedTypes.includes(file.type)) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Archivo no permitido',
                    detail: `${file.name} no es un formato válido. Solo se permiten PDF e imágenes.`
                });
                return false;
            }
            return true;
        });

        if (validFiles.length > 0) {
            setFiles([...files, ...validFiles]);
        }

        // Clear invalid files from the UI component immediately
        if (validFiles.length !== e.files.length) {
            fileUploadRef.current.clear();
            // Re-add valid files if any were accepted (a bit tricky with PrimeReact controlled mode, 
            // but since we manage 'files' state separately, simply clearing visual component is safer 
            // to remove the invalid visual item, though it clears valid ones visually too. 
            // Ideally we just don't add them to our state 'files' which is what matters for submission)
        }
    };

    const handleUpload = async () => {
        if (amount <= 0) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Ingresa un monto válido' });
            return;
        }
        if (files.length === 0) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Sube al menos un comprobante' });
            return;
        }

        if (amount > remaining) { // +1 tolerance for rounding differences
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'El monto no puede ser mayor al restante' });
            return;
        }

        const formData = new FormData();
        formData.append('amount', amount);
        files.forEach(file => {
            formData.append('files[]', file);
        });

        setUploading(true);
        try {
            const response = await axios.post(`${url}/user-dashboard/upload-payment`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    Accept: 'application/json'
                }
            });

            if (response.data.success) {
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: response.data.message });
                setAmount(0);
                setFiles([]);
                fileUploadRef.current.clear();
                onUpdate();
            }
        } catch (error) {
            console.error('Error uploading payment:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo subir el pago' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="flex flex-column gap-4 p-2">
            <div className="flex flex-column md:flex-row gap-3">
                <div className="flex-1 bg-blue-50 p-3 border-round-xl">
                    <span className="text-xs font-semibold text-blue-600 uppercase">Monto Total</span>
                    <h2 className="text-2xl font-bold m-0 text-blue-900">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(roundedDownPayment)}
                    </h2>
                </div>
                <div className="flex-1 bg-green-50 p-3 border-round-xl">
                    <span className="text-xs font-semibold text-green-600 uppercase">Abonado</span>
                    <h2 className="text-2xl font-bold m-0 text-green-900">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalPaid)}
                    </h2>
                </div>
                <div className="flex-1 bg-orange-50 p-3 border-round-xl">
                    <span className="text-xs font-semibold text-orange-600 uppercase">Restante</span>
                    <h2 className="text-2xl font-bold m-0 text-orange-900">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(remaining)}
                    </h2>
                </div>
            </div>

            {/* Status Messages */}
            <div className="flex flex-column gap-2">
                {hasPending && (
                    <div className="bg-yellow-50 border-left-3 border-yellow-500 p-3 flex align-items-center gap-3">
                        <i className="pi pi-clock text-yellow-600 text-xl"></i>
                        <div>
                            <span className="font-bold block text-yellow-800">Evaluando Pago</span>
                            <span className="text-sm text-yellow-700">El administrador está evaluando tu pago inicial. Te avisaremos pronto.</span>
                        </div>
                    </div>
                )}

                {lastPayment?.status === 'rejected' && !hasPending && (
                    <div className="bg-red-50 border-left-3 border-red-500 p-3 flex align-items-center gap-3">
                        <i className="pi pi-times-circle text-red-600 text-xl"></i>
                        <div>
                            <span className="font-bold block text-red-800">Pago Rechazado</span>
                            <span className="text-sm text-red-700">El administrador rechazó tu pago inicial: {lastPayment.comment}</span>
                        </div>
                    </div>
                )}

                {remaining > 0 && !hasPending && (
                    <div className="bg-blue-50 border-left-3 border-blue-500 p-3 flex align-items-center gap-3">
                        <i className="pi pi-info-circle text-blue-600 text-xl"></i>
                        <div>
                            <span className="font-bold block text-blue-800">Pendiente de completar</span>
                            <span className="text-sm text-blue-700">Aún tienes un saldo pendiente. Sube tu próximo comprobante abajo.</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Upload Form */}
            {!hasPending && remaining > 0 && (
                <div className="border-1 border-gray-200 border-round-xl p-4">
                    <h3 className="text-lg font-bold mb-3">Subir Nuevo Comprobante</h3>
                    <div className="grid">
                        <div className="col-12 md:col-6 mb-3">
                            <label className="block text-sm font-semibold mb-2">Monto del Comprobante</label>
                            <InputNumber
                                value={amount}
                                onValueChange={(e) => {
                                    let val = e.value;
                                    // Just round for precision, no clamping/toast here
                                    if (val) {
                                        val = Math.round(val * 100) / 100;
                                    }
                                    setAmount(val);
                                }}
                                mode="currency"
                                currency="MXN"
                                locale="es-MX"
                                // max={remaining} // Removed to allow handling the error manually
                                placeholder="$ 0.00"
                                className={isAmountInvalid ? 'p-invalid w-full' : 'w-full'}
                            />
                            {isAmountInvalid && <small className="p-error block mt-1">El monto no puede ser mayor al restante ({new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(remaining)})</small>}
                        </div>
                        <div className="col-12 mb-3">
                            <label className="block text-sm font-semibold mb-2">Adjuntar Comprobante (PDF o Imagen)</label>
                            <FileUpload
                                ref={fileUploadRef}
                                name="demo[]"
                                multiple
                                accept="image/*,application/pdf,image/webp"
                                maxFileSize={10000000}
                                customUpload={true}
                                onSelect={onSelectFile}
                                onRemove={(e) => {
                                    setFiles(files.filter(f => f !== e.file));
                                }}
                                onClear={() => setFiles([])}
                                emptyTemplate={
                                    <p className="m-0 text-center p-4 text-gray-500">
                                        <i className="pi pi-cloud-upload text-3xl block mb-2"></i>
                                        Arrastra archivos adicionales aquí o haz clic para seleccionar
                                    </p>
                                }
                                chooseLabel="Seleccionar archivos"
                                className="mt-3"
                                headerTemplate={<></>}
                                itemTemplate={(file, props) => {
                                    const removeFile = () => {
                                        if (props && props.onRemove) {
                                            props.onRemove();
                                        }
                                        const newFiles = files.filter(f => f.name !== file.name || f.size !== file.size);
                                        setFiles(newFiles);
                                    };

                                    return (
                                        <div className="flex align-items-center justify-content-between p-2 border-bottom-1 border-gray-200">
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-file text-blue-500"></i>
                                                <span className="text-sm font-medium">{file.name}</span>
                                                <span className="text-sm text-gray-500">({(file.size / 1024).toFixed(2)} KB)</span>
                                            </div>
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                onClick={removeFile}
                                                tooltip="Eliminar archivo"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        </div>
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-content-end mt-2">
                        <Button
                            label={uploading ? "Subiendo..." : "Enviar Comprobante"}
                            icon={uploading ? "pi pi-spin pi-spinner" : "pi pi-send"}
                            className="Button Green w-full md:w-auto px-5"
                            onClick={handleUpload}
                            disabled={uploading || amount <= 0 || files.length === 0 || isAmountInvalid}
                        />
                    </div>
                </div>
            )}

            {/* Payment History */}
            {step?.payments?.length > 0 && (
                <div className="mt-2">
                    <h3 className="text-lg font-bold mb-3">Historial de Pagos</h3>
                    <div className="flex flex-column gap-2">
                        {step.payments.map((p, i) => (
                            <div key={i} className="flex justify-content-between align-items-center p-2 bg-gray-50 border-round">
                                <div className="flex flex-column">
                                    <span className="font-bold">
                                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(p.amount)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {new Date(p.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                {p.status === 'rejected' && (
                                    <Button
                                        label="Ver motivo"
                                        className="p-button-text p-button-danger p-button-sm py-1 px-2 text-xs"
                                        onClick={() => setReasonConfig({ visible: true, text: p.comment })}
                                    />
                                )}
                                <div className="flex align-items-center gap-2">
                                    {p.status === 'pending' && <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 border-round font-bold uppercase">Evaluando</span>}
                                    {p.status === 'complete' && <span className="bg-green-100 text-green-800 text-xs px-2 py-1 border-round font-bold uppercase">Verificado</span>}
                                    {p.status === 'incomplete' && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 border-round font-bold uppercase">Incompleto</span>}
                                    {p.status === 'rejected' && <span className="bg-red-100 text-red-800 text-xs px-2 py-1 border-round font-bold uppercase">Rechazado</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {/* Reason Dialog */}
            <Dialog
                header="Motivo del rechazo"
                visible={reasonConfig.visible}
                onHide={() => setReasonConfig({ ...reasonConfig, visible: false })}
                style={{ width: '90vw', maxWidth: '500px' }}
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                dismissableMask
                draggable={false}
            >
                <div className="p-3">
                    <p className="m-0 text-gray-700 line-height-3" style={{ whiteSpace: 'pre-wrap' }}>
                        {reasonConfig.text}
                    </p>
                    <div className="flex justify-content-end mt-4">
                        <Button label="Entendido" onClick={() => setReasonConfig({ ...reasonConfig, visible: false })} className="p-button-text" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
